import { Matrix4, Mesh, Object3D, Vector3, type Vector3Like } from 'three';
import { ParticleEmitterID } from './ParticleEmitterConstants';
import ParticleEmitterCore, {
  type ParticleEmitterCoreOptions,
  type ParticleEmitterEmissionOverrides,
} from './ParticleEmitterCore';
import Entity from '../entities/Entity';
import { EntityId } from '../entities/EntityConstants';
import Game from '../Game';
import Assets from '../network/Assets';
import type { CustomTextureWrapper } from '../textures/CustomTextureManager';
import { updateAABB } from '../three/utils';

export interface ParticleEmitterEmitOptions {
  entityId?: EntityId;
  entityNodeName?: string;
  offset?: Vector3Like;
  position?: Vector3Like;
}

export interface ParticleEmitterPlayOptions {
  count?: number;
  rate?: number;
}

export interface AttachmentSource {
  entityId?: EntityId;
  entityNodeName?: string;
  offset?: Vector3Like;
  position?: Vector3Like;
}

export interface ActivePlay {
  handle: number;
  source: AttachmentSource;
  attachmentSlot: number;
  overrides?: ParticleEmitterEmissionOverrides;
  count?: number;
  rate?: number;
  accumulator: number;
  pendingCount: number;
}

export interface ParticleEmitterOptions {
  id: ParticleEmitterID;
  attachedToEntityId?: EntityId | null;
  attachedToEntityNodeName?: string | null;
  textureUri: string;
  emitterCoreOptions?: ParticleEmitterCoreOptions;
  position?: Vector3Like;
  offset?: Vector3Like;
}

export default class ParticleEmitter {
  private _game: Game;
  private _id: ParticleEmitterID;
  private _attachedToEntityId: EntityId | null = null;
  private _attachedToEntityNode: Object3D | null = null;
  private _attachedToEntityNodeName: string | null = null;
  private _emitterCore: ParticleEmitterCore;
  private _position: Vector3 = new Vector3();
  private _offset: Vector3 = new Vector3();
  private _pendingTextureWrappers: Set<Promise<CustomTextureWrapper>> = new Set();
  private _textureUri: string = '';
  private _textureWrapper: CustomTextureWrapper | null = null;

  // Play/stop handle system
  private _activePlays: Map<number, ActivePlay> = new Map();
  private _attachmentSlotsByKey: Map<string, number> = new Map();
  private _attachmentKeyBySlot: Map<number, string> = new Map();
  private _attachmentSources: Map<number, AttachmentSource> = new Map();
  private _freeAttachmentSlots: number[] = [];
  private _nextAttachmentSlot: number = 1;
  private _attachmentMatrixScratch: Matrix4 = new Matrix4();
  private _transformDirty: boolean = true;

  constructor(game: Game, options: ParticleEmitterOptions) {
    this._game = game;
    this._id = options.id;

    this._emitterCore = new ParticleEmitterCore(options.emitterCoreOptions || {});
    this._emitterCore.mesh.matrixAutoUpdate = false;
    this._emitterCore.mesh.matrixWorldAutoUpdate = false;

    if (options.position) {
      this.setPosition(options.position);
    }

    if (options.offset) {
      this.setOffset(options.offset);
    }

    if (options.attachedToEntityId !== undefined) {
      this.setAttachedToEntityId(options.attachedToEntityId);
    }

    if (options.attachedToEntityNodeName !== undefined) {
      this.setAttachedToEntityNodeName(options.attachedToEntityNodeName);
    }

    this.setTextureUri(options.textureUri);

    this._game.renderer.addToScene(this._emitterCore.mesh);
  }

  public get id(): ParticleEmitterID {
    return this._id;
  }

  public get mesh(): Mesh {
    return this._emitterCore.mesh;
  }

  public get hasDynamicPlaySources(): boolean {
    return this._activePlays.size > 0;
  }

  public burst(count: number, emitOptions?: ParticleEmitterEmitOptions, overrides?: ParticleEmitterEmissionOverrides): void {
    this._syncWorldTransformIfNeeded();

    // Determine attachment slot based on emit options
    const attachmentSlot = emitOptions ? this._allocateAttachmentSlot(emitOptions) : 0;

    if (attachmentSlot > 0) {
      const source = this._attachmentSources.get(attachmentSlot);
      if (source) {
        const matrix = this._resolveAttachmentMatrix(source);
        if (matrix) {
          this._emitterCore.setAttachmentMatrix(attachmentSlot, matrix);
        } else {
          return;
        }
      }
    } else {
      this._emitterCore.setAttachmentMatrix(0, this._emitterCore.mesh.matrixWorld);
    }

    this._emitterCore.burst(count, attachmentSlot, overrides);
  }

  public pause(): void {
    this._emitterCore.pause();
  }

  public restart(): void {
    this._emitterCore.restart();
  }

  public play(
    handle: number,
    emitOptions?: ParticleEmitterEmitOptions,
    overrides?: ParticleEmitterEmissionOverrides,
    playOptions?: ParticleEmitterPlayOptions,
  ): void {
    const source: AttachmentSource = emitOptions ? { ...emitOptions } : {};

    const existingPlay = this._activePlays.get(handle);
    if (existingPlay) {
      const sameSource = this._sourceToKey(existingPlay.source) === this._sourceToKey(source);
      const sameOverrides = this._playOverridesToKey(existingPlay.overrides) === this._playOverridesToKey(overrides);
      const sameCount = existingPlay.count === playOptions?.count;
      const sameRate = existingPlay.rate === playOptions?.rate;

      if (sameSource && sameOverrides && sameCount && sameRate) {
        return;
      }
    }

    // Stop existing play with same handle
    if (this._activePlays.has(handle)) {
      this.stop(handle);
    }

    const attachmentSlot = this._allocateAttachmentSlot(source, false);

    const activePlay: ActivePlay = {
      handle,
      source,
      attachmentSlot,
      overrides,
      count: playOptions?.count,
      rate: playOptions?.rate,
      accumulator: 0,
      pendingCount: 0,
    };

    this._activePlays.set(handle, activePlay);
    this._attachmentSources.set(attachmentSlot, source);
    this._updateCullingMode();

    // If count is specified, emit immediately
    if (activePlay.count !== undefined && activePlay.count > 0) {
      this._syncWorldTransformIfNeeded();
      if (!this._emitFromSource(activePlay.count, source, attachmentSlot, overrides)) {
        activePlay.pendingCount += activePlay.count;
      }
    }
  }

  public stop(handle: number): void {
    const play = this._activePlays.get(handle);
    if (!play) return;

    // Kill particles for this attachment slot
    this._emitterCore.killAttachmentSlot(play.attachmentSlot);

    // Release the slot
    this._releaseAttachmentSlot(play.attachmentSlot);

    this._activePlays.delete(handle);
    this._updateCullingMode();
  }

  public stopAll(): void {
    for (const handle of Array.from(this._activePlays.keys())) {
      this.stop(handle);
    }
  }

  private _allocateAttachmentSlot(source: AttachmentSource, reuseExisting: boolean = true): number {
    let key: string | undefined;

    // Reuse existing slot if same source
    if (reuseExisting) {
      key = this._sourceToKey(source);
      if (this._attachmentSlotsByKey.has(key)) {
        return this._attachmentSlotsByKey.get(key)!;
      }
    }

    if (this._freeAttachmentSlots.length === 0) {
      this._reclaimInactiveAttachmentSlots();
    }

    const slot = this._freeAttachmentSlots.length > 0 ? this._freeAttachmentSlots.pop()! : this._nextAttachmentSlot++;

    if (reuseExisting && key !== undefined) {
      this._attachmentSlotsByKey.set(key, slot);
      this._attachmentKeyBySlot.set(slot, key);
    }

    this._attachmentSources.set(slot, source);
    return slot;
  }

  private _reclaimInactiveAttachmentSlots(): void {
    if (this._attachmentKeyBySlot.size === 0) {
      return;
    }

    const activeSlots = new Set(this._emitterCore.getActiveAttachmentSlots());
    const activePlaySlots = new Set<number>();
    for (const play of this._activePlays.values()) {
      activePlaySlots.add(play.attachmentSlot);
    }

    const releasableSlots: number[] = [];
    for (const slot of this._attachmentKeyBySlot.keys()) {
      if (activeSlots.has(slot) || activePlaySlots.has(slot)) {
        continue;
      }
      releasableSlots.push(slot);
    }

    for (const slot of releasableSlots) {
      this._releaseAttachmentSlot(slot);
    }
  }

  private _releaseAttachmentSlot(slot: number): void {
    const key = this._attachmentKeyBySlot.get(slot);
    if (key) {
      this._attachmentSlotsByKey.delete(key);
    }
    this._attachmentKeyBySlot.delete(slot);
    this._attachmentSources.delete(slot);

    if (slot > 0) {
      this._freeAttachmentSlots.push(slot);
    }
  }

  private _sourceToKey(source: AttachmentSource): string {
    return JSON.stringify({
      e: source.entityId,
      n: source.entityNodeName,
      o: source.offset,
      p: source.position,
    });
  }

  private _playOverridesToKey(overrides?: ParticleEmitterEmissionOverrides): string {
    if (!overrides) {
      return '';
    }

    return JSON.stringify(overrides);
  }

  private _resolveAttachmentMatrix(source: AttachmentSource): Matrix4 | null {
    const matrix = this._attachmentMatrixScratch.identity();

    if (source.entityId !== undefined) {
      const entity = this._game.entityManager.getEntity(source.entityId);
      if (!entity) {
        return null;
      }

      if (source.entityNodeName) {
        const node = entity.entityRoot.getObjectByName(source.entityNodeName);
        if (node) {
          node.updateWorldMatrix(true, false);
          matrix.copy(node.matrixWorld);
        } else {
          entity.entityRoot.updateWorldMatrix(true, false);
          matrix.copy(entity.entityRoot.matrixWorld);
        }
      } else {
        entity.entityRoot.updateWorldMatrix(true, false);
        matrix.copy(entity.entityRoot.matrixWorld);
      }
    } else if (source.position) {
      matrix.setPosition(source.position.x, source.position.y, source.position.z);
    } else {
      matrix.copy(this._emitterCore.mesh.matrixWorld);
    }

    if (source.offset) {
      matrix.setPosition(
        matrix.elements[12] + source.offset.x,
        matrix.elements[13] + source.offset.y,
        matrix.elements[14] + source.offset.z,
      );
    }

    return matrix;
  }

  private _emitFromSource(
    count: number,
    source: AttachmentSource,
    attachmentSlot: number,
    overrides?: ParticleEmitterEmissionOverrides,
  ): boolean {
    // Update attachment matrix
    const matrix = this._resolveAttachmentMatrix(source);
    if (!matrix) {
      return false;
    }
    this._emitterCore.setAttachmentMatrix(attachmentSlot, matrix);

    this._emitterCore.burst(count, attachmentSlot, overrides);
    return true;
  }

  private _updateActivePlays(deltaTimeS: number): void {
    for (const play of this._activePlays.values()) {
      // Update attachment matrix for this play
      const matrix = this._resolveAttachmentMatrix(play.source);
      if (matrix) {
        this._emitterCore.setAttachmentMatrix(play.attachmentSlot, matrix);

        if (play.pendingCount > 0) {
          this._emitterCore.burst(play.pendingCount, play.attachmentSlot, play.overrides);
          play.pendingCount = 0;
        }
      }

      // If rate is defined, emit particles over time
      if (play.rate !== undefined && play.rate > 0) {
        play.accumulator += play.rate * deltaTimeS;
        const particlesToEmit = Math.floor(play.accumulator);
        if (particlesToEmit > 0) {
          if (matrix) {
            this._emitterCore.burst(particlesToEmit, play.attachmentSlot, play.overrides);
          } else {
            play.pendingCount += particlesToEmit;
          }
          play.accumulator -= particlesToEmit;
        }
      }
    }

    // Sync instance matrices for lockToEmitter mode
    this._emitterCore.syncLockedParticleInstanceMatrices();
  }

  public setPosition(position: Vector3Like): void {
    this._position.copy(position);
    this._transformDirty = true;
  }

  public setOffset(offset: Vector3Like): void {
    this._offset.copy(offset);
    this._transformDirty = true;
  }

  public setVisible(visible: boolean): void {
    this._emitterCore.mesh.visible = visible;
  }

  private _getAttachedToEntity(): Entity | null {
    const attachedToEntity = this._game.entityManager.getEntity(this._attachedToEntityId!);
    if (!attachedToEntity) {
      console.warn(`ParticleEmitter._getAttachedToEntity(): Attached To Entity ${this._attachedToEntityId} is not found.`);
      return null;
    }
    return attachedToEntity;
  }

  private _attachToEntity(): void {
    const attachedToEntity = this._getAttachedToEntity();
    attachedToEntity?.addModelReadyListener(this._attachedToEntityModelReadyCallback, !!this._attachedToEntityNodeName);
  }

  private _detachFromEntity(): void {
    const attachedToEntity = this._getAttachedToEntity();
    attachedToEntity?.removeModelReadyListener(this._attachedToEntityModelReadyCallback);
    this._attachedToEntityNode = null;
  }

  public setAttachedToEntityId(attachedToEntityId: EntityId | null): void {
    if (this._attachedToEntityId === attachedToEntityId) {
      return;
    }

    if (this._attachedToEntityId !== null) {
      this._detachFromEntity();
    }

    this._attachedToEntityId = attachedToEntityId;

    if (this._attachedToEntityId !== null) {
      this._attachToEntity();
    }

    // Particles emitted after emission do not follow the entity attached to's position.
    // Because of this, if the entity attached to moves, it's very difficult to perform
    // efficient frustum culling on the CPU. As a result, we disable CPU-side
    // frustum culling in this case.
    // TODO: Either solve the root problem or relax the condition. e.g., only disable
    // frustum culling when the entity attached to's position has changed.
    // TODO: Enabling frustum culling may need to be done more carefully. For example,
    // if the position changes frequently or significantly, already emitted particles may
    // be unintentionally culled.
    this._updateCullingMode();
    this._transformDirty = true;
  }

  public setAttachedToEntityNodeName(attachedToEntityNodeName: string | null): void {
    if (this._attachedToEntityNodeName === attachedToEntityNodeName) {
      return;
    }

    this._attachedToEntityNodeName = attachedToEntityNodeName;

    if (this._attachedToEntityId !== null) {
      this._detachFromEntity();
      this._attachToEntity();
    }

    this._transformDirty = true;
  }

  private _attachedToEntityModelReadyCallback = (attachedToEntity: Entity): void => {
    const attachedToEntityNode = (this._attachedToEntityNodeName !== null) ? attachedToEntity.entityRoot.getObjectByName(this._attachedToEntityNodeName) : attachedToEntity.entityRoot

    if (!attachedToEntityNode) {
      console.warn(`ParticleEmitter._attachedToEntityModelReadyCallback(): Node ${this._attachedToEntityNodeName} is not found in Entity ${this._attachedToEntityId}. Falling back to entity root.`);
      this._attachedToEntityNode = attachedToEntity.entityRoot;
      this._transformDirty = true;
      return;
    }

    // TODO: Holding a reference to the Object3D inside an Entity is probably not a good design.
    // Switch to a better approach.
    this._attachedToEntityNode = attachedToEntityNode;
    this._transformDirty = true;
  };

  public setEmitterCoreOptions(options: ParticleEmitterCoreOptions): void {
    this._emitterCore.updateParameters(options);
  }

  public setTextureUri(textureUri: string): void {
    const newUri = textureUri.startsWith('data:') ? textureUri : Assets.toAssetUri(textureUri);

    if (this._textureUri === newUri) {
      return;
    }

    this._textureUri = newUri;
    this._loadTexture();
  }

  private async _loadTexture(): Promise<void> {
    this._pendingTextureWrappers.forEach(pendingTextureWrapper => {
      this._game.customTextureManager.cancel(pendingTextureWrapper, true);
    });
    this._pendingTextureWrappers.clear();

    const loadingTextureUri = this._textureUri;

    const pendingTextureWrapper = this._game.customTextureManager.load(loadingTextureUri);
    this._pendingTextureWrappers.add(pendingTextureWrapper);
    let textureWrapper: CustomTextureWrapper;
    try {
      textureWrapper = await pendingTextureWrapper;
    } catch (error) {
      this._pendingTextureWrappers.delete(pendingTextureWrapper);
      console.warn(`ParticleEmitter._loadTexture(): Failed to load texture "${loadingTextureUri}".`, error);
      return;
    }

    if (!this._pendingTextureWrappers.has(pendingTextureWrapper)) {
      this._game.customTextureManager.release(textureWrapper);
      return;
    }

    this._pendingTextureWrappers.delete(pendingTextureWrapper);

    if (this._textureWrapper) {
      this._game.customTextureManager.release(this._textureWrapper);
    }

    this._textureWrapper = textureWrapper;
    this._emitterCore.updateParameters({ texture: textureWrapper.texture });
  }

  public update(deltaTimeS: number): void {
    this._syncWorldTransformIfNeeded();
    this._updateActivePlays(deltaTimeS);
    this._emitterCore.update(deltaTimeS);
  }

  private _updateCullingMode(): void {
    // Per-play attachments can distribute particles far from emitter mesh origin.
    // Keep frustum culling off while active plays exist to avoid incorrect culling.
    this._emitterCore.mesh.frustumCulled = !this.hasDynamicPlaySources && this._attachedToEntityId === null;
  }

  private _syncWorldTransformIfNeeded(): void {
    if (!this._transformDirty && this._attachedToEntityNode === null) {
      return;
    }

    const mesh = this._emitterCore.mesh;
    const prevX = mesh.position.x;
    const prevY = mesh.position.y;
    const prevZ = mesh.position.z;

    if (this._attachedToEntityNode !== null) {
      // TODO: What if the entity attached to has been already removed?
      this._attachedToEntityNode.getWorldPosition(mesh.position);
    } else {
      mesh.position.copy(this._position);
    }

    mesh.position.add(this._offset);

    if (
      !this._transformDirty &&
      mesh.position.x === prevX &&
      mesh.position.y === prevY &&
      mesh.position.z === prevZ
    ) {
      return;
    }

    mesh.updateMatrix();
    mesh.matrixWorld.copy(mesh.matrix);
    mesh.matrixWorldNeedsUpdate = false;
    this._emitterCore.setAttachmentMatrix(0, mesh.matrixWorld);
    updateAABB(mesh);

    this._transformDirty = false;
  }

  public dispose(): void {
    if (this._attachedToEntityId !== null) {
      const attachedToEntity = this._game.entityManager.getEntity(this._attachedToEntityId);
      attachedToEntity?.removeModelReadyListener(this._attachedToEntityModelReadyCallback);
      this._attachedToEntityNode = null;
      this._attachedToEntityId = null;
    }

    this._pendingTextureWrappers.forEach(pendingTextureWrapper => {
      this._game.customTextureManager.cancel(pendingTextureWrapper, true);
    });
    this._pendingTextureWrappers.clear();

    if (this._textureWrapper) {
      this._game.customTextureManager.release(this._textureWrapper);
      this._textureWrapper = null;
    }

    // Clear all active plays
    this._activePlays.clear();
    this._attachmentSlotsByKey.clear();
    this._attachmentKeyBySlot.clear();
    this._attachmentSources.clear();
    this._freeAttachmentSlots.length = 0;

    this._emitterCore.dispose();
    this._game.renderer.removeFromScene(this._emitterCore.mesh);
  }
}
